import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useSettings } from '../../contexts/SettingsContext';
// import { PDFDownloadLink } from '@react-pdf/renderer';
import { DeliveryInvoicePDF } from './DeliveryInvoicePDF.jsx';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { apiService } from '../../services/apiService';

interface DeliveryInvoice {
  _id: string;
  invoiceNumber: string;
  type: string;
  status: string;
  customer: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  project?: {
    _id: string;
    name: string;
    address?: string;
  };
  workOrder?: {
    number?: string;
    date?: string;
  };
  deliveryAddress?: string;
  dispatchedThrough?: {
    name: string;
    phone?: string;
  };
  items: Array<{
    product?: {
      _id: string;
      name: string;
      description?: string;
      unit?: string;
    };
    name?: string;
    brand?: string;
    model?: string;
    sku?: string;
    type?: string;
    compressorType?: string;
    countryOfOrigin?: string;
    countryOfAssembly?: string;
    details?: string;
    description?: string;
    capacity?: string;
    serialNo?: string;
    partNo?: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    totalPrice: number;
    warrantyDetails?: {
      hasWarranty: boolean;
      serviceWarranty?: number | null;
      sparePartsWarranty?: number | null;
      compressorWarranty?: number | null;
      warranty?: number | null;
    };
  }>;
  subtotal: number;
  discount: number;
  total: number;
  createdAt: string;
  date?: string;
}

interface PrintViewProps {
  invoice: DeliveryInvoice;
  settings: any;
  currentPage: number;
  itemsPerPage: number;
  printMode?: boolean;
}

interface DeliveryInvoicePDFButtonProps {
  invoice: any;
  settings: any;
}

// export const DeliveryInvoicePDFButton = ({ invoice, settings }: DeliveryInvoicePDFButtonProps) => (
//   <PDFDownloadLink
//     document={<DeliveryInvoicePDF invoice={invoice} settings={settings} />}
//     fileName={`DeliveryInvoice-${invoice.invoiceNumber || 'download'}.pdf`}
//     style={{ textDecoration: 'none' } as any}
//   >
//     {({ loading }) => (
//       <button
//         className="no-print"
//         style={{
//           padding: '6px 16px',
//           background: '#388e3c',
//           color: '#fff',
//           border: 'none',
//           borderRadius: 4,
//           cursor: 'pointer',
//           fontWeight: 'bold',
//           fontSize: '14px',
//           fontFamily: 'var(--font-family)',
//         } as any}
//         disabled={loading}
//       >
//         {loading ? 'Preparing PDF...' : 'Download PDF'}
//       </button>
//     )}
//   </PDFDownloadLink>
// );

const DeliveryInvoicePrintView: React.FC<PrintViewProps> = ({ invoice, settings, currentPage, itemsPerPage, printMode }) => {
  // Move all hooks to the top
  const { settings: globalSettings, loading } = useSettings();
  
  if (loading || !invoice) return <div>Loading...</div>;

  const logoUrl = globalSettings?.company?.logo ? `/uploads/${globalSettings.company.logo}` : '/The Aircons Ltd. Logo Org.png';
  
  const company = globalSettings?.company || {};
  const deliverySettings = globalSettings?.deliveryInvoice || {};

  const isInvoiceBlank = invoice.status === 'blank' || (invoice.items && invoice.items.length > 0 && invoice.items.every((item: any) => !item.product && !item.name && !item.brand && !item.model));

  const isRowVisible = (idx: number) => {
    if (printMode) return true;
    const startIdx = (currentPage - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    return idx >= startIdx && idx < endIdx;
  };
  const printTime = new Date().toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'medium' });
  const totalPages = Math.ceil((invoice.items?.length || 0) / itemsPerPage) || 1;
  const itemsPerPagePrint = 25;

  // Helper function to format product description
  const getProductDescription = (item: any) => {
    // Check if this is a blank row (no product data)
    const isBlankRow = !item.product && !item.name && !item.brand && !item.model;
    if (isBlankRow) {
      return ''; // Return empty string for blank rows
    }
    
    const fieldMap = [
      { key: 'name', label: 'Product Name' },
      { key: 'brand', label: 'Brand' },
      { key: 'model', label: 'Model' },
      { key: 'sku', label: 'SKU' },
      { key: 'type', label: 'Type' },
      { key: 'compressorType', label: 'Compressor Type' },
      { key: 'countryOfOrigin', label: 'Country of Origin' },
      { key: 'countryOfAssembly', label: 'Country of Assembly' },
      { key: 'details', label: 'Details' },
      { key: 'description', label: 'Description' },
      { key: 'remarks', label: 'Note' }
    ];
    const descLines = fieldMap
      .filter(f => item[f.key] && item[f.key] !== '-')
      .map(f => <div key={f.key}><b>{f.label}:</b> {item[f.key]}</div>);

    return descLines.length > 0 ? <>{descLines}</> : <div>-</div>;
  };

  const customerNameStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '11.5pt' } as any;
  const customerAddressStyle: React.CSSProperties = { fontSize: '11.5pt' } as any;
  const customerPhoneStyle: React.CSSProperties = { fontSize: '11.5pt' } as any;
  const customerEmailStyle: React.CSSProperties = { fontSize: '11.5pt' } as any;
  const projectInfoStyle: React.CSSProperties = { fontWeight: 'bold', borderTop: '1px solid #000', borderBottom: '1px solid #000', margin: '16px 0 4px 0', fontSize: '11.5pt', paddingTop: 4 } as any;
  const projectNameStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '11.5pt' } as any;
  const deliveryAddressStyle: React.CSSProperties = { fontSize: '11.5pt' } as any;

  // Add variables for all style objects used in the JSX
  const invoiceHeaderRowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: 0, width: 'auto', padding: 0 } as any;
  const logoContainerStyle: React.CSSProperties = { flex: '0 0 auto', paddingLeft: 48, paddingTop: 32 } as any;
  const companyInfoStyle: React.CSSProperties = { flex: '1 1 auto', textAlign: 'right', marginLeft: 16, paddingRight: 48, paddingTop: 32 } as any;
  const companyInfoTextStyle: React.CSSProperties = { fontSize: '9.75pt' } as any;
  const invoiceTitleStyle: React.CSSProperties = { textAlign: 'center', fontWeight: 'bold', marginBottom: 4 } as any;
  const invoiceDetailsRowStyle: React.CSSProperties = { display: 'flex', border: '1px solid #000', borderBottom: 'none' } as any;
  const customerDetailsColStyle: React.CSSProperties = { flex: 1, borderRight: '1px solid #000', padding: '8px' } as any;
  const deliveryDetailsColStyle: React.CSSProperties = { flex: 1, padding: '8px' } as any;
  const customerInfoTitleStyle: React.CSSProperties = { fontWeight: 'bold', borderBottom: '1px solid #000', marginBottom: 4, fontSize: '11.5pt' } as any;
  const deliveryInfoTitleStyle: React.CSSProperties = { fontWeight: 'bold', borderBottom: '1px solid #000', marginBottom: 4, fontSize: '11.5pt' } as any;
  const deliveryInfoRowStyle: React.CSSProperties = { display: 'flex' } as any;
  const deliveryInfoLabelStyle: React.CSSProperties = { flex: 1, fontWeight: 'bold', fontSize: '11.5pt' } as any;
  const deliveryInfoValueStyle: React.CSSProperties = { flex: 1, fontSize: '11.5pt' } as any;
  const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginBottom: 0 } as any;
  const thSLStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '32px', textAlign: 'center', fontSize: '11.5pt' } as any;
  const thDescStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', textAlign: 'left' } as any;
  const thQtyStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '60px', textAlign: 'center', fontSize: '11.5pt' } as any;
  const thUnitStyle: React.CSSProperties = { border: '1px solid #000', padding: '3pt', width: '60px', textAlign: 'center', fontSize: '11.5pt' } as any;
  const trStyle: React.CSSProperties = { verticalAlign: 'top' } as any;
  const tdSLStyle: React.CSSProperties = { border: '1px solid #000', padding: '5pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt' } as any;
  const tdDescStyle: React.CSSProperties = { border: '1px solid #000', padding: '5pt', verticalAlign: 'top', whiteSpace: 'pre-line', fontSize: '11pt' } as any;
  const tdQtyStyle: React.CSSProperties = { border: '1px solid #000', padding: '5pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt' } as any;
  const tdUnitStyle: React.CSSProperties = { border: '1px solid #000', padding: '5pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt' } as any;
  
  // Styles for blank rows with extra height
  const tdSLStyleBlank: React.CSSProperties = { border: '1px solid #000', padding: '12pt 5pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt', minHeight: '35pt' } as any;
  const tdDescStyleBlank: React.CSSProperties = { border: '1px solid #000', padding: '12pt 5pt', verticalAlign: 'top', whiteSpace: 'pre-line', fontSize: '11pt', minHeight: '35pt' } as any;
  const tdQtyStyleBlank: React.CSSProperties = { border: '1px solid #000', padding: '12pt 5pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt', minHeight: '35pt' } as any;
  const tdUnitStyleBlank: React.CSSProperties = { border: '1px solid #000', padding: '12pt 5pt', textAlign: 'center', verticalAlign: 'top', fontSize: '11.5pt', minHeight: '35pt' } as any;
  const summaryRowStyle: React.CSSProperties = { display: 'flex', border: '1px solid #000', borderTop: 'none', alignItems: 'center', minHeight: '20px' } as any;
  const summaryColStyle: React.CSSProperties = { flex: 1, padding: '1.5pt 3pt', borderRight: '1px solid #000', fontWeight: 'bold', fontSize: '8.25pt', lineHeight: '1.1' } as any;
  const summaryColRightStyle: React.CSSProperties = { flex: 1, textAlign: 'right', padding: '1.5pt 3pt', fontWeight: 'bold', fontSize: '8.25pt', lineHeight: '1.1' } as any;
  const bottomSectionStyle: React.CSSProperties = { pageBreakInside: 'avoid', breakInside: 'avoid' } as any;
  const bottomTableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000', marginTop: 0, background: '#fff' } as any;
  const bottomTdLeftStyle: React.CSSProperties = { verticalAlign: 'top', textAlign: 'left', fontSize: '10.5pt', width: '40%', padding: '0 6pt' } as any;
  const bottomTdQRStyle: React.CSSProperties = { borderBottom: '1px solid #000', borderLeft: 'none', borderLeftWidth: 0, verticalAlign: 'middle', textAlign: 'center', width: '70px', minWidth: '70px', maxWidth: '70px', padding: 0 } as any;
  const qrContainerStyle: any = { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', padding: 0, margin: 0 };
  const qrStyle: React.CSSProperties = { width: '60%', height: '60%', display: 'block', margin: 0, padding: 0 } as any;
  const bottomTdRightStyle: React.CSSProperties = { verticalAlign: 'top', textAlign: 'right', fontSize: '10.5pt', width: '40%', padding: '0 6pt' } as any;
  const bottomTrStyle: React.CSSProperties = { minHeight: 32 } as any;
  const bottomTdSignStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '10.875pt', padding: '0 6pt 6pt 6pt', verticalAlign: 'bottom' } as any;
  const bottomTdSignRightStyle: React.CSSProperties = { fontWeight: 'bold', fontSize: '10.875pt', padding: '0 6pt 6pt 6pt', textAlign: 'right', verticalAlign: 'bottom' } as any;
  const bottomFooterStyle: React.CSSProperties = { fontSize: '9pt', color: '#222', marginTop: 8, display: 'flex', justifyContent: 'space-between', width: '100%' } as any;
  const printTimeStyle: React.CSSProperties = { textAlign: 'right' } as any;

  const renderInvoiceContent = (pageItems: any[], pageIndex: number, totalPagesCount: number) => (
    <div
      key={pageIndex}
      className="bg-white print-invoice-area print-active delivery-invoice-print-container"
      id={printMode ? `delivery-invoice-print-root-${pageIndex}` : undefined}
      style={{
        fontFamily: 'Times New Roman, serif',
        fontSize: '9pt',
        color: '#000',
        boxSizing: 'border-box',
        margin: 0,
        padding: '0.1in 0 0 0',
        position: 'relative',
        background: '#fff',
        borderRadius: 0,
        boxShadow: 'none',
        overflow: 'visible',
        paddingBottom: 0,
        ...(printMode && pageIndex !== 0 ? { breakAfter: 'page' } : {}),
      } as any}
    >
      <div className="invoice-header-row" style={invoiceHeaderRowStyle}>
        <div style={logoContainerStyle}>
          <img
            src={logoUrl}
            alt="Invoice Logo"
            height={40}
            style={{ height: 40, width: 'auto', display: 'block' } as any}
            className="invoice-logo"
            onError={(e) => {
              console.error('Failed to load delivery invoice logo from:', (e.target as HTMLImageElement).src);
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        <div className="company-info" style={companyInfoStyle}>
          <div style={companyInfoTextStyle}>Plot-14, Block-C, Main Road, Banasree, Rampura, Dhaka-1219.</div>
          <div style={companyInfoTextStyle}>Phone: +880-1749-121607 | Email: support@theaircons.com</div>
          <div style={companyInfoTextStyle}>Website: https://www.theaircons.com</div>
        </div>
      </div>
      <div style={{ padding: '0 40px', margin: 0 } as any}>
        <div className="invoice-title" style={invoiceTitleStyle}>Delivery Invoice</div>
        <div style={invoiceDetailsRowStyle}>
          <div style={customerDetailsColStyle}>
            <div style={customerInfoTitleStyle}>Customer Information</div>
            <div style={customerNameStyle}>{invoice.customer?.name || 'Customer Name'}</div>
            <div style={customerAddressStyle}>{invoice.customer?.address || 'Customer Address'}</div>
            <div style={customerPhoneStyle}>Phone: {invoice.customer?.phone || ''}</div>
            <div style={customerEmailStyle}>Email: {invoice.customer?.email || ''}</div>
            <div style={projectInfoStyle}>Project Information</div>
            <div style={projectNameStyle}>{invoice.project?.name || 'Project Name'}</div>
            <div style={deliveryAddressStyle}>Delivery Address: {invoice.deliveryAddress || invoice.customer?.address || ''}</div>
          </div>
          <div style={deliveryDetailsColStyle}>
            <div style={deliveryInfoTitleStyle}>Delivery Information</div>
            <div style={deliveryInfoRowStyle}>
              <div style={deliveryInfoLabelStyle}>Delivery Invoice No.</div>
              <div style={deliveryInfoValueStyle}>{invoice.invoiceNumber}</div>
            </div>
            <div style={deliveryInfoRowStyle}>
              <div style={deliveryInfoLabelStyle}>Purchase Date</div>
              <div style={deliveryInfoValueStyle}>{isInvoiceBlank ? '' : (invoice.date ? new Date(invoice.date).toLocaleDateString() : new Date(invoice.createdAt).toLocaleDateString())}</div>
            </div>
            <div style={deliveryInfoRowStyle}>
              <div style={deliveryInfoLabelStyle}>Dispatched through</div>
              <div style={deliveryInfoValueStyle}>{invoice.dispatchedThrough?.name || ''}</div>
            </div>
            <div style={deliveryInfoRowStyle}>
              <div style={deliveryInfoLabelStyle}>Mobile No.</div>
              <div style={deliveryInfoValueStyle}>{invoice.dispatchedThrough?.phone || ''}</div>
            </div>
            <div style={projectInfoStyle}>Work Order Information</div>
            <div style={deliveryInfoRowStyle}>
              <div style={deliveryInfoLabelStyle}>Work Order No.</div>
              <div style={deliveryInfoValueStyle}>{invoice.workOrder?.number || ''}</div>
            </div>
            <div style={deliveryInfoRowStyle}>
              <div style={deliveryInfoLabelStyle}>Work Order Date</div>
              <div style={deliveryInfoValueStyle}>{invoice.workOrder?.date ? new Date(invoice.workOrder.date).toLocaleDateString() : ''}</div>
            </div>
          </div>
        </div>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thSLStyle}>SL</th>
              <th style={thDescStyle}>Description of Goods</th>
              <th style={thQtyStyle}>Quantity</th>
              <th style={thUnitStyle}>Unit</th>
            </tr>
          </thead>
          <tbody>
            {pageItems && pageItems.length > 0 ? (
              (() => {
                let itemNumber = 0; // Counter for non-blank items only
                return pageItems.map((item, idx) => {
                  const visibleClass = isRowVisible(idx) ? 'visible' : '';
                  const desc = getProductDescription(item);
                  const isBlankRow = !item.product && !item.name && !item.brand && !item.model;
                  
                  // Only increment item number for non-blank rows
                  if (!isBlankRow) {
                    itemNumber++;
                  }
                  const displayNumber = isBlankRow ? '' : itemNumber;
                  
                  return (
                    <tr key={idx} className={`invoice-row ${visibleClass}`} style={trStyle}>
                      <td style={isBlankRow ? tdSLStyleBlank : tdSLStyle}>{displayNumber}</td>
                      <td style={isBlankRow ? tdDescStyleBlank : tdDescStyle}>{desc}</td>
                      <td style={isBlankRow ? tdQtyStyleBlank : tdQtyStyle}>{isBlankRow ? '' : (item.quantity || '')}</td>
                      <td style={isBlankRow ? tdUnitStyleBlank : tdUnitStyle}>{isBlankRow ? '' : (item.unit || '')}</td>
                    </tr>
                  );
                });
              })()
            ) : (
              <tr className="invoice-row visible" style={trStyle}>
                <td style={tdSLStyle}>1</td>
                <td style={tdDescStyle}>No products added</td>
                <td style={tdQtyStyle}>-</td>
                <td style={tdUnitStyle}>-</td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={summaryRowStyle}>
          <div style={summaryColStyle}>Total {pageItems?.length || 0} Items</div>
          <div style={summaryColRightStyle}>E. & O.E</div>
        </div>

        {/* Aggregated Warranty Details */}
        {pageItems.some(item => item.warrantyDetails?.hasWarranty) && (
          <div style={{ marginTop: '8px', border: '1px solid #000', padding: '8px', fontSize: '10.5pt' }}>
            <div style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: '4px', fontSize: '11pt' }}>Warranty Details</div>
            {pageItems.filter(item => item.warrantyDetails?.hasWarranty).map((item, idx) => {
              const formatWarranty = (years: number) => `${years} ${years === 1 ? 'Year' : 'Years'}`;
              const warranties = [];
              if (item.warrantyDetails!.serviceWarranty) {
                warranties.push(`Service Warranty: ${formatWarranty(item.warrantyDetails!.serviceWarranty)}`);
              }
              if (item.warrantyDetails!.sparePartsWarranty) {
                warranties.push(`Spare Parts Warranty: ${formatWarranty(item.warrantyDetails!.sparePartsWarranty)}`);
              }
              if (item.warrantyDetails!.compressorWarranty) {
                warranties.push(`Compressor Warranty: ${formatWarranty(item.warrantyDetails!.compressorWarranty)}`);
              }
              if (item.warrantyDetails!.warranty) {
                warranties.push(`Warranty: ${formatWarranty(item.warrantyDetails!.warranty)}`);
              }

              if (warranties.length === 0) return null;

              return (
                <div key={idx} style={{ marginBottom: '4px' }}>
                  <b>{item.name || (item.product && item.product.name) || 'Product'}:</b> {warranties.join(', ')}
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom section and footer: only render once at the very end, keep together */}
        {(printMode ? (pageIndex === totalPagesCount - 1) : (currentPage === totalPagesCount)) && (
          <div style={bottomSectionStyle}>
            <table
              className="invoice-bottom-section"
              style={bottomTableStyle}
            >
              <tbody>
                <tr>
                  <td style={bottomTdLeftStyle}>Received in Good Condition.</td>
                  <td rowSpan={2} style={bottomTdQRStyle}>
                    <div style={qrContainerStyle as any}>
                      <QRCodeSVG value={`https://aircons-inventory-backend.onrender.com/api/invoices/delivery-invoices/${invoice._id}/pdf`} style={qrStyle as any} />
                    </div>
                  </td>
                  <td style={bottomTdRightStyle}>for The Aircons Ltd.</td>
                </tr>
                <tr style={bottomTrStyle}>
                  <td style={bottomTdSignStyle}>Customer's Signature</td>
                  <td style={bottomTdSignRightStyle}>Authorised Signatory</td>
                </tr>
              </tbody>
            </table>
            <div style={bottomFooterStyle}>
              <span>This is a computer-generated document.</span>
              <span style={printTimeStyle}>Print Time<br />{printTime}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (printMode) {
    // Split items into pages for print
    const pages = [];
    for (let i = 0; i < invoice.items.length; i += itemsPerPagePrint) {
      pages.push(invoice.items.slice(i, i + itemsPerPagePrint));
    }
    return (
      <div
        className="invoice-container"
        style={{
          margin: '24px auto',
          maxWidth: '210mm',
          background: '#fff',
          padding: 0,
        } as any}
      >
        <style>{`
          @page { margin: 0; }
          /* @page removed for Safari compatibility */
          .invoice-row { display: none; }
          @media screen { .invoice-row.visible { display: table-row; } }
          @media print { .invoice-row { display: table-row !important; } .print:hidden { display: none !important; } }
          .invoice-bottom-section { page-break-inside: avoid; break-inside: avoid; }
          .print-invoice-area, .print-invoice-area * {
            font-size: 9pt !important;
          }
          .invoice-title {
            font-size: 14pt !important;
            font-weight: bold !important;
          }
          @media print {
            @page { size: A4 portrait; margin: 10mm; }
            body, html {
              width: 210mm;
              height: 297mm;
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            .print-invoice-area, .print-invoice-area * {
              font-size: 9pt !important;
            }
            .invoice-title {
              font-size: 14pt !important;
              font-weight: bold !important;
            }
            .invoice-header-row { margin: 0 !important; padding: 0 !important; }
            .invoice-header-row > div { margin: 0 !important; padding-top: 0 !important; }
            .invoice-logo {
              height: 40px !important;
              width: auto !important;
              max-height: 40px !important;
            }
          }
          @media print {
            .invoice-container {
              margin: 0 auto 10mm auto !important;
              max-width: 210mm !important;
              background: #fff !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
            }
          }
          
          /* Mobile-specific print styles */
          @media print and (max-width: 768px) {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            body, html {
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              box-sizing: border-box !important;
            }
            
            .invoice-container {
              width: 100% !important;
              display: block !important;
              position: relative !important;
              overflow: visible !important;
              height: auto !important;
              min-height: auto !important;
              max-height: none !important;
              transform: none !important;
              opacity: 1 !important;
              z-index: 9999 !important;
              margin: 0 !important;
              background: #fff !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
            }
            
            .invoice-container * {
              visibility: visible !important;
            }
            
            .print-invoice-area, .print-invoice-area * {
              font-size: 8pt !important;
              line-height: 1.2 !important;
              color: #000 !important;
              visibility: visible !important;
            }
            
            table {
              display: table !important;
              visibility: visible !important;
              position: static !important;
              overflow: visible !important;
              font-size: 7pt !important;
            }
            
            tr {
              display: table-row !important;
              visibility: visible !important;
            }
            
            td, th {
              display: table-cell !important;
              visibility: visible !important;
              padding: 1px 2px !important;
            }
            
            img {
              display: block !important;
              visibility: visible !important;
            }
          }
        `}</style>
        {/* Email button removed */}
        {pages.map((pageItems, pageIndex) => renderInvoiceContent(pageItems, pageIndex, pages.length))}
      </div>
    );
  }

  // Main (non-print) view
  const currentItems = (invoice.items || []).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  return (
    <div className="bg-white print-invoice-area print-active" id={printMode ? 'delivery-invoice-print-root' : undefined} style={{ fontFamily: 'Times New Roman, serif', fontSize: '9pt', color: '#000', boxSizing: 'border-box', margin: 0, padding: 0, position: 'relative', background: '#fff', borderRadius: 0, boxShadow: 'none', overflow: 'hidden', paddingBottom: 0 } as any}>
      <style>{`
        @page { margin: 0; }
        /* @page removed for Safari compatibility */
        .invoice-row { display: none; }
        @media screen { .invoice-row.visible { display: table-row; } }
        @media print { .invoice-row { display: table-row !important; } .print:hidden { display: none !important; } }
        .invoice-bottom-section { page-break-inside: avoid; break-inside: avoid; }
        .print-invoice-area, .print-invoice-area * {
          font-size: 9pt !important;
        }
        .invoice-title {
          font-size: 14pt !important;
          font-weight: bold !important;
        }
        @media print {
          body, html {
            width: 210mm;
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          .print-invoice-area, .print-invoice-area * {
            font-size: 9pt !important;
          }
          .invoice-title {
            font-size: 14pt !important;
            font-weight: bold !important;
          }
          .invoice-header-row { margin: 0 !important; padding: 0 !important; }
          .invoice-header-row > div { margin: 0 !important; padding-top: 0 !important; }
          .invoice-logo {
            height: 40px !important;
            width: auto !important;
            max-height: 40px !important;
          }
        }
        @media print {
          .invoice-container {
            margin: 0 auto 10mm auto !important;
            max-width: 210mm !important;
            background: #fff !important;
            padding: 0.1in 0 0 0 !important;
            border: none !important;
            box-shadow: none !important;
          }
        }
      `}</style>
      {renderInvoiceContent(currentItems, 0, totalPages)}
    </div>
  );
};

export default DeliveryInvoicePrintView; 